import { useId, type ReactNode } from 'react';
import { Panel } from '@src/components/Panel/Panel';
import { testIdAttr, testIdFor } from '@src/components/testId';
import { Typography } from '@src/components/Typography/Typography';
import './ChoicePanel.css';

export interface ChoicePanelProps {
  title?: string;
  description?: string;
  /** Decorative slot above the title — festive marks, a gift, nothing. */
  illustration?: ReactNode;
  /** The Choice rows. */
  children: ReactNode;
  /** Usually a full-width primary Button. */
  action?: ReactNode;
  /** Skip the card chrome when this already sits inside a Panel. */
  plain?: boolean;
  /** Parent id. Title, choices and action hang off it. */
  testId?: string;
  className?: string;
}

export function ChoicePanel({
  title,
  description,
  illustration,
  children,
  action,
  plain = false,
  testId,
  className,
}: ChoicePanelProps) {
  const titleId = useId();
  const classes = ['choice-panel'];
  if (className) {
    classes.push(className);
  }

  const body = (
    <>
      {(illustration || title || description) && (
        <header className="choice-panel__intro">
          {illustration && <div className="choice-panel__illustration">{illustration}</div>}
          {title && (
            <Typography
              as="h2"
              role="title/section"
              className="choice-panel__title"
              id={titleId}
              {...testIdAttr(testIdFor(testId, 'title'))}
            >
              {title}
            </Typography>
          )}
          {description && (
            <Typography
              as="p"
              role="body/lead"
              className="choice-panel__description"
              {...testIdAttr(testIdFor(testId, 'description'))}
            >
              {description}
            </Typography>
          )}
        </header>
      )}

      <div
        className="choice-panel__choices"
        role="radiogroup"
        aria-labelledby={title ? titleId : undefined}
        {...testIdAttr(testIdFor(testId, 'choices'))}
      >
        {children}
      </div>

      {action && (
        <div className="choice-panel__action" {...testIdAttr(testIdFor(testId, 'action'))}>
          {action}
        </div>
      )}
    </>
  );

  if (plain) {
    return (
      <div className={classes.join(' ')} {...testIdAttr(testId)}>
        {body}
      </div>
    );
  }

  return (
    <Panel padding="medium" className={classes.join(' ')} testId={testId}>
      {body}
    </Panel>
  );
}
